import { StatusPage } from "./Status";
import { BlockEditor } from "../components/block-editor";
import { UserPicker } from "../components/UserPicker";
import { GripVertical, Pencil, Plus, Power, Search, WalletCards, X } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AdditionalPermission, api, Department, Project, ProjectBudget, ProjectBudgetCommand, ProjectDocumentSummary, ProjectResource, ProjectWorkflow, ResearchNote, ScopePerformance, Session, Task, User,
  WorkflowStage, WorkflowStageCommand, WorkflowStageKind, WorkItem,
} from "../api";
import { initials, isoDate, noteStatusText, workflowKindText } from "../lib/format";
import { userBelongsTo, userWithinDepartment } from "../lib/org";
import { Avatar, ConfirmDialog, DataTable, Empty, Loading, Meter, ModalFrame, PageHead, RequirementHint, focusInvalidField, RowActionMenu, SpineField, TableFoot } from "../components/ui";
import { taskStateText } from "./Tasks";
import type { TaskSeed } from "../lib/types";

/** 직접 작성하는 프로젝트 문서의 초안. id가 있으면 기존 문서를 고치는 중이다. */
type DocumentDraft = { id?: string; title: string; label: string; body: string };

type DocumentRow = {
  key: string; title: string; label: string; who: string; at: string; state: string; tone: "" | "warn" | "muted";
  open?: () => void;
};

const statusText: Record<Project["status"], string> = { ACTIVE: "진행", COMPLETED: "완료", CANCELLED: "취소" };

export function ProjectsPage({ session, permissions, projects, tasks, canCreate, onOpen, onCreate, onBudget }: {
  session: Session; permissions?: AdditionalPermission[]; projects: Project[]; tasks: Task[]; canCreate: boolean; onOpen: (id: string) => void; onCreate: () => void;
  onBudget: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const standalone = tasks.filter((task) => !task.projectId).length;
  const visible = projects.filter((project) => `${project.code} ${project.name} ${project.projectType || ""}`.toLowerCase().includes(query.trim().toLowerCase()));
  const canSeeBudgetManager = session.roles.includes("OWNER")
    || Boolean(permissions?.some((permission) => permission === "PROJECT_BUDGET_VIEW" || permission === "PROJECT_BUDGET_EDIT"))
    || projects.some((project) => project.members.some((member) => member.userId === session.userId && member.role === "PM"));

  return <>
    <PageHead title={t("projects.title")} meta={t("projects.meta")} controls={<>
      <div className="search-field"><Search size={14} /><input aria-label={t("projects.search")} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("projects.search")} /></div>
      {canSeeBudgetManager && <button type="button" onClick={onBudget}><WalletCards size={15} /> {t("projects.budget")}</button>}
      {canCreate && <button className="primary" onClick={onCreate}><Plus size={15} /> {t("projects.create")}</button>}
    </>} />
    <div className="table-card">
      <div className="project-grid project-grid-head">
        <span>{t("projects.code")}</span><span>{t("projects.project")}</span><span>{t("projects.pm")}</span><span>{t("projects.taskCount")}</span><span>{t("projects.progress")}</span><span data-col-optional="">{t("projects.period")}</span><span>{t("projects.status")}</span>
      </div>
      {visible.map((project) => {
        const projectTasks = tasks.filter((task) => task.projectId === project.id);
        const done = projectTasks.filter((task) => task.status === "COMPLETED").length;
        const pct = projectTasks.length ? Math.round(done / projectTasks.length * 100) : 0;
        const pm = project.members.find((member) => member.role === "PM");
        // 행 전체가 클릭 대상이다. 키보드는 제목 버튼으로 들어간다.
        return <div className="project-grid project-grid-row" key={project.id} onClick={() => onOpen(project.id)}>
          <span className="mono">{project.code}</span>
          <span className="title-cell"><button type="button" onClick={() => onOpen(project.id)}>{project.name}<small>{project.projectType || t("projects.general")}</small></button></span>
          <span>{pm?.displayName || t("projects.unassigned")}</span>
          <span className="mono">{t("projects.count", { count: projectTasks.length })}</span>
          <span className="cell-meter"><Meter ratio={pct} /><span className="mono">{pct}%</span></span>
          <span data-col-optional="" className="mono">{isoDate(project.startsOn)} → {isoDate(project.endsOn)}</span>
          <span className={`state-cell project-${project.status.toLowerCase()}`}><i />{t(`projectStatus.${project.status}`, { defaultValue: statusText[project.status] })}</span>
        </div>;
      })}
      {visible.length === 0 && <Empty title={t("projects.empty")} copy={t("projects.emptyHelp")} />}
      {visible.length > 0 && <TableFoot count={visible.length} total={projects.length} hint={t("projects.standalone", { count: standalone })} />}
    </div>
  </>;
}

export function ProjectDetail({ id, session, users, departments, notes, page = "overview", documentId, workId, intake, onOpenIntake, onOpenWork, renderTask, onBack, onTask, onCreateTask, onOpenNote, onBudget, onPage, onChanged }: {
  id: string; session: Session; users: User[]; departments: Department[]; notes: ResearchNote[];
  onBack: () => void; onTask: (taskId: string) => void; onCreateTask: (seed: TaskSeed) => void;
  onOpenNote: (noteId: string) => void;
  onBudget: () => void;
  onChanged?: (project: Project) => void;
  workId?: string; onOpenWork?: (id?: string) => void; renderTask?: (id: string, onChanged: () => void) => import("react").ReactNode;
  intake?: import("../lib/routes").StatusTaskRoute; onOpenIntake?: (intake?: import("../lib/routes").StatusTaskRoute) => void;
  page?: "overview" | "board" | "document-new" | "document-edit"; documentId?: string;
  onPage: (page: "overview" | "board" | "document-new" | "document-edit", documentId?: string) => void;
}) {
  const { t } = useTranslation();
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [workflow, setWorkflow] = useState<ProjectWorkflow | undefined>();
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [resources, setResources] = useState<ProjectResource[]>([]);
  const [writtenDocs, setWrittenDocs] = useState<ProjectDocumentSummary[]>([]);
  const [performance, setPerformance] = useState<ScopePerformance | null>(null);
  const [editing, setEditing] = useState(false);
  const [workflowAttempted, setWorkflowAttempted] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);
  const [resourceAdding, setResourceAdding] = useState(false);
  const [overviewEditing, setOverviewEditing] = useState(false);
  const [docMenu, setDocMenu] = useState(false);
  const [confirming, setConfirming] = useState<"COMPLETED" | "CANCELLED" | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [composing, setComposing] = useState<DocumentDraft | null>(null);
  const [draftStages, setDraftStages] = useState<WorkflowStageCommand[]>([]);
  const [docLabel, setDocLabel] = useState("전체 라벨");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [nextProject, nextTasks, nextWorkflow, nextWorkItems, nextResources, nextDocuments] = await Promise.all([
        api.project(id), api.projectTasks(id), api.projectWorkflow(id),
        api.projectWorkItems(id), api.projectResources(id), api.projectDocuments(id),
      ]);
      setProject(nextProject); setTasks(nextTasks); setWorkflow(nextWorkflow);
      setWorkItems(nextWorkItems); setResources(nextResources); setWrittenDocs(nextDocuments);
      api.projectPerformance(id, isoDate(nextProject.startsOn), new Date().toISOString().slice(0, 10)).then(setPerformance).catch(() => setPerformance(null));
    } catch (reason) { setError(reason instanceof Error ? reason.message : "불러오지 못했습니다."); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    let active = true;
    if (page === "document-new") {
      setComposing({ title: "", label: "", body: "" });
      return () => { active = false; };
    }
    if (page === "document-edit" && documentId) {
      setComposing(null);
      void api.projectDocument(id, documentId).then((full) => {
        if (active) setComposing({ id: full.id, title: full.title, label: full.label || "", body: full.body });
      }).catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : "문서를 열지 못했습니다."); });
      return () => { active = false; };
    }
    setComposing(null);
    return () => { active = false; };
  }, [documentId, id, page]);

  const userName = useMemo(() => new Map(users.map((user) => [user.id, user.displayName])), [users]);
  if (!project) return <main className="page"><div className="breadcrumb"><button type="button" onClick={onBack}>← 프로젝트</button></div>{error ? <div className="global-error">{error}</div> : <Loading layout="detail" label="프로젝트를 불러오는 중" />}</main>;

  const done = tasks.filter((task) => task.status === "COMPLETED").length;
  const awaiting = tasks.filter((task) => task.rounds.at(-1)?.status === "SUBMITTED").length;
  const pct = tasks.length ? Math.round(done / tasks.length * 100) : 0;
  const pm = project.members.find((member) => member.role === "PM");
  const isPm = project.members.some((member) => member.userId === session.userId && member.role === "PM");
  const canManageSensitive = session.roles.includes("OWNER") || isPm;
  const canEdit = project.status === "ACTIVE" && (session.roles.includes("OWNER") || isPm);
  const departmentName = (departmentId?: string | null) => departments.find((department) => department.id === departmentId)?.name;
  const openTasks = tasks.filter((task) => task.status !== "COMPLETED" && task.status !== "CANCELLED").length;


  const projectNotes = notes.filter((note) => note.sourceTaskIds.some((taskId) => tasks.some((task) => task.id === taskId)));
  const documents: DocumentRow[] = [
    ...writtenDocs.map((document) => ({
      key: document.id, title: document.title, label: document.label || "작성 문서",
      who: userName.get(document.createdBy) || "-", at: isoDate(document.updatedAt),
      state: "작성 문서", tone: "" as const,
      // 문서를 누르면 같은 작성 화면이 본문을 채운 채로 열린다.
      open: () => onPage("document-edit", document.id),
    })),
    ...resources.map((resource) => ({
      key: resource.id, title: resource.title, label: resource.label || "링크",
      who: userName.get(resource.createdBy) || "-", at: isoDate(resource.createdAt),
      state: "링크", tone: "muted" as const,
      open: () => window.open(resource.externalUrl || "", "_blank", "noreferrer"),
    })),
    ...tasks.map((task) => ({
      key: `task-${task.id}`, title: task.internalTitle, label: "업무 공간",
      who: userName.get(task.issuerId) || "-", at: isoDate(task.confirmedAt) || isoDate(task.startsOn),
      state: `${task.rounds.length + 1}개 기록 · ${taskStateText(task)}`, tone: task.status === "DRAFT" ? "muted" as const : "" as const,
      open: () => onTask(task.id),
    })),
    ...projectNotes.map((note) => {
      const version = note.versions.at(-1);
      return {
        key: `note-${note.id}`, title: version?.content.title || "연구노트", label: "연구노트",
        who: version?.content.authorName || "-", at: isoDate(version?.content.authoredOn),
        state: noteStatusText[note.status], tone: note.status === "AWAITING_REVIEW" ? "warn" as const : "" as const,
        open: () => onOpenNote(note.id),
      };
    }),
  ].sort((left, right) => (right.at || "").localeCompare(left.at || ""));
  const labels = ["전체 라벨", ...new Set(documents.map((document) => document.label))];
  const visibleDocuments = docLabel === "전체 라벨" ? documents : documents.filter((document) => document.label === docLabel);

  const stageRows = [
    ...(workflow?.stages || []).map((stage) => {
      // 하위 업무는 단계에 직접 붙지 않는다. 상위 업무를 거쳐 모인다.
      const stageItems = workItems.filter((item) => item.workflowStageId === stage.id);
      const stageTaskIds = new Set(stageItems.flatMap((item) => item.taskIds));
      const stageTasks = tasks.filter((task) => stageTaskIds.has(task.id));
      const stagePerformance = performance?.workflowStages.find((item) => item.stageId === stage.id);
      return {
        id: stage.id, name: stage.name, kind: workflowKindText[stage.kind],
        owner: stage.kind === "TEAM" ? departmentName(stage.departmentId) || "팀 미지정" : stage.assigneeIds.map((userId) => userName.get(userId)).filter(Boolean).join(", ") || "담당 미지정",
        tasks: stageTasks,
        count: stagePerformance?.taskCount ?? stageTasks.length,
        completed: stagePerformance?.completedCount ?? stageTasks.filter((task) => task.status === "COMPLETED").length,
        overdue: stagePerformance?.overdueCount ?? 0,
        stage,
      };
    }),
    ...(tasks.some((task) => !task.workItemId) ? [{
      id: "unstaged", name: "단계 미지정", kind: "—", owner: "—",
      tasks: tasks.filter((task) => !task.workItemId),
      count: tasks.filter((task) => !task.workItemId).length,
      completed: tasks.filter((task) => !task.workItemId && task.status === "COMPLETED").length,
      overdue: 0, stage: undefined as WorkflowStage | undefined,
    }] : []),
  ];
  const bottleneck = [...stageRows].filter((row) => row.overdue > 0).sort((left, right) => right.overdue - left.overdue)[0];

  const beginEdit = () => {
    setDraftStages(workflow?.stages.map((stage) => ({ clientKey: stage.clientKey, name: stage.name, kind: stage.kind, assigneeIds: stage.assigneeIds, departmentId: stage.departmentId, publisherDepartmentIds: stage.publisherDepartmentIds, autoAdvance: stage.autoAdvance })) || [
      { clientKey: "work", name: "실행", kind: "WORK", assigneeIds: [] },
      { clientKey: "complete", name: "완료", kind: "COMPLETE", assigneeIds: [] },
    ]);
    setWorkflowAttempted(false); setEditing(true);
  };
  const teamStageMissingDepartment = draftStages.some((stage) => stage.kind === "TEAM" && !stage.departmentId);
  const workflowMissing = [draftStages.length < 2 ? "시작·완료를 포함한 최소 2개 단계" : "", teamStageMissingDepartment ? "팀 실행 단계의 담당 팀" : ""].filter(Boolean);
  const activeDepartments = departments.filter((department) => department.status === "ACTIVE");
  /**
   * 팀 실행 단계의 부서는 프로젝트 참여 부서여야 백엔드 검증을 통과한다. 참여 부서를 따로 고르는
   * 화면을 없앴으므로, 노드에서 고른 팀을 저장 직전에 참여 부서로 합쳐 넣는다.
   */
  const saveWorkflow = async () => {
    setBusy(true); setError("");
    try {
      const teamDepartmentIds = draftStages.map((stage) => stage.departmentId).filter(Boolean) as string[];
      const missing = teamDepartmentIds.filter((departmentId) => !project.participatingDepartmentIds.includes(departmentId));
      if (missing.length) await api.replaceProjectDepartments(project.id, [...new Set([...project.participatingDepartmentIds, ...missing])]);
      await api.replaceProjectWorkflow(project.id, draftStages);
      setEditing(false);
      await load();
    }
    catch (reason) { setError(reason instanceof Error ? reason.message : "워크플로우를 저장하지 못했습니다."); }
    finally { setBusy(false); }
  };

  if ((page === "document-new" || page === "document-edit") && !composing) {
    return <main className="page"><div className="breadcrumb"><button type="button" onClick={() => onPage("overview")}>← {project.name}</button></div>{error ? <div className="global-error">{error}</div> : <Loading layout="detail" label="문서를 불러오는 중" />}</main>;
  }

  if (composing) return <DocumentComposer
    draft={composing}
    projectName={project.name}
    busy={busy}
    error={error}
    onChange={setComposing}
    onCancel={() => { setError(""); onPage("overview"); }}
    onSave={async (draft) => {
      setBusy(true); setError("");
      try {
        const body = { title: draft.title.trim(), label: draft.label.trim() || null, body: draft.body };
        if (draft.id) await api.updateProjectDocument(project.id, draft.id, body);
        else await api.createProjectDocument(project.id, body);
        await load(); onPage("overview");
      } catch (reason) { setError(reason instanceof Error ? reason.message : "문서를 저장하지 못했습니다."); }
      finally { setBusy(false); }
    }}
  />;

  if (page === "board") return <main className="page project-board-page">
    {workflow || workItems.length > 0 ? <StatusPage tasks={tasks} users={users} departments={departments} projects={[project]} session={session}
      canManage={session.roles.some(role => role === "OWNER" || role === "MANAGER")} dashboardId={project.id}
      workId={workId} onOpenWork={onOpenWork} renderTask={renderTask}
      intake={intake} onOpenIntake={onOpenIntake}
      onSelect={() => onPage("overview")} onOpenTask={onTask} onChanged={async () => { await load(); }}
      backLabel="프로젝트 상세" />
      : <><button type="button" className="back" onClick={() => onPage("overview")}>← 프로젝트 상세</button><Empty title="워크플로우를 먼저 만들어 주세요" /></>}
  </main>;

  return <main className="page">
    <div className="page-head detail-head">
      <div>
        <div className="breadcrumb"><button type="button" onClick={onBack}>← 프로젝트</button></div>
        <h1>{project.name}</h1>
        <div className="page-head-meta">
          <span>{t(`projectStatus.${project.status}`)}</span><span className="dot">·</span>
          <span>책임자 {pm?.displayName || "미지정"}</span><span className="dot">·</span>
          <span className="mono">{isoDate(project.startsOn)} → {isoDate(project.endsOn)}</span><span className="dot">·</span>
          <span>업무 {tasks.length}건 중 {done}건 완료</span>
        </div>
      </div>
      <div className="page-head-controls">
        {canEdit && <button type="button" onClick={beginEdit}>{workflow ? "워크플로우 수정" : "워크플로우 만들기"}</button>}
        <button type="button" onClick={() => onPage("board")}>프로젝트 실행 보드</button>
        {canManageSensitive && <RowActionMenu label="프로젝트 메뉴" actions={[
          { label: t("projects.budget"), onClick: onBudget },
          ...(canEdit ? (["COMPLETED", "CANCELLED"] as const).map(status => ({
            label: t(status === "COMPLETED" ? "projects.complete" : "projects.cancel"),
            danger: status === "CANCELLED", disabled: openTasks > 0,
            title: openTasks > 0 ? t("projects.openFirst", { count: openTasks }) : undefined,
            onClick: () => setConfirming(status),
          })) : []),
          { label: "삭제", danger: true, disabled: openTasks > 0,
            title: openTasks > 0 ? t("projects.openFirst", { count: openTasks }) : undefined,
            onClick: () => setDeleteConfirm(true) },
        ]} />}
      </div>
    </div>
    {error && <div className="global-error">{error}</div>}

    <section className="section-card">
      <header>
        <div><span className="mono-label">OVERVIEW</span><h2>개요</h2></div>
        {canEdit && <button type="button" className="secondary" onClick={() => setOverviewEditing(true)}><Pencil size={13} /> 수정</button>}
      </header>
      <div className="spine project-spine">
        <SpineField label="[제목]">{project.name}</SpineField>
        <SpineField label="[목표]"><div className="longform">{project.goal || "목표가 입력되지 않았습니다."}</div></SpineField>
        <SpineField label="[설명]"><div className="longform">{project.description || "설명이 입력되지 않았습니다."}</div></SpineField>
      </div>
    </section>

    <section className="section-card">
      <header>
        <div><span className="mono-label">PROGRESS</span><h2>현황</h2></div>
        <span className="pill muted">{workflow ? `워크플로우 v${workflow.version}` : "워크플로우 없이 실행 중"}</span>
      </header>
      <div className="card-stats">
        <div className="card-stat">
          <span className="mono-label">진행률</span>
          <strong>{pct}%</strong>
          <Meter ratio={pct} />
          <small>업무 {tasks.length}건 중 {done}건 완료</small>
        </div>
        <div className="card-stat">
          <span className="mono-label">판정 대기</span>
          <strong>{awaiting}</strong>
          <small>제출 후 판정을 기다리는 회차</small>
        </div>
        <div className="card-stat">
          <span className="mono-label">납기 준수율</span>
          <strong>{performance?.onTimeRate === null || performance?.onTimeRate === undefined ? "-" : `${Math.round(performance.onTimeRate)}%`}</strong>
          <small>{performance ? "마감일 대비 실제 완료일" : "집계 권한이 없거나 완료 업무가 없습니다"}</small>
        </div>
        <div className="card-stat">
          <span className="mono-label">문서</span>
          <strong>{writtenDocs.length + resources.length + projectNotes.length}</strong>
          <small>공용 문서·링크·연구노트 기준 · 업무 공간 {tasks.length}개</small>
        </div>
      </div>

      <div className="span-section">
        <header><b>구간</b><span className="mono-label">단계별 업무 수와 완료·지연입니다.</span></header>
        {stageRows.length > 0 && <DataTable cols="minmax(200px, 2fr) minmax(140px, 1fr) 80px 150px 80px">
          <div className="data-row data-head"><span>단계</span><span>담당</span><span>업무</span><span>완료</span><span>지연</span></div>
          {stageRows.map((row) => <div className="data-row" key={row.id}>
            <span className="title-cell"><b>{row.name}</b><small>{row.kind}</small></span>
            <span>{row.owner}</span>
            <span className="mono">{row.count}건</span>
            <span className="cell-meter"><span className="mono">{row.completed}/{row.count}</span><Meter ratio={row.count ? row.completed / row.count * 100 : 0} /></span>
            <span className={row.overdue ? "mono warn" : "mono muted"}>{row.overdue}건</span>
          </div>)}
        </DataTable>}
        {stageRows.length === 0 && <Empty title="워크플로우 없이 실행 중입니다" copy="필요하면 PM이 실행 단계를 설계할 수 있습니다. 업무는 자동으로 생성되지 않습니다." />}
        {bottleneck && <p className="bottleneck">병목 · {bottleneck.name} · 지연 {bottleneck.overdue}건 / 업무 {bottleneck.count}건</p>}
      </div>
    </section>

    <section className="section-card">
      <header>
        <div><span className="mono-label">DOCUMENTS</span><h2>문서</h2></div>
        <div className="header-tools">
          <select aria-label="라벨 필터" value={docLabel} onChange={(event) => setDocLabel(event.target.value)}>{labels.map((label) => <option value={label} key={label}>{label}</option>)}</select>
          {canEdit && <span className="row-menu add-doc-menu">
            <button type="button" className="secondary" aria-expanded={docMenu} onClick={() => setDocMenu(!docMenu)}><Plus size={14} /> 문서 추가</button>
            {docMenu && <span className="row-menu-list">
              <button type="button" onClick={() => { setDocMenu(false); onPage("document-new"); }}>작성</button>
              <button type="button" onClick={() => { setDocMenu(false); setResourceAdding(true); }}>외부 링크</button>
            </span>}
          </span>}
        </div>
      </header>
      <DataTable cols="minmax(240px, 2fr) 92px minmax(110px, 1fr) 110px 96px">
        <div className="data-row data-head"><span>제목</span><span>라벨</span><span>작성자</span><span>일시</span><span>상태</span></div>
        {visibleDocuments.map((document) => <div className="data-row doc-row" key={document.key}>
          <span className="title-cell">{document.open ? <button type="button" onClick={document.open}>{document.title}</button> : <b>{document.title}</b>}</span>
          <span><em className={`doc-label label-${document.label === "연구노트" ? "note" : document.label === "지시서" ? "directive" : document.label === "결과 보고" ? "report" : "plain"}`}>{document.label}</em></span>
          <span>{document.who}</span>
          <span className="mono">{document.at || "-"}</span>
          <span className={document.tone === "warn" ? "warn" : document.tone === "muted" ? "muted" : ""}>{document.state}</span>
        </div>)}
      </DataTable>
      {visibleDocuments.length === 0 && <Empty title="문서가 없습니다" copy="공용 문서는 프로젝트에 작성하고, 업무별 참고자료와 산출물은 각 업무 공간에 정리하세요." />}
    </section>

    {editing && <ModalFrame title="워크플로우 설계" eyebrow="VERSIONED WORKFLOW" wide onClose={() => setEditing(false)}><div className={`workflow-editor ${workflowAttempted && workflowMissing.length ? "field-missing" : ""}`}>
      <p>저장할 때 새 버전이 생성됩니다. 완료 단계는 항상 마지막에 유지됩니다.</p>

      <WorkflowCanvas
        stages={draftStages}
        onChange={setDraftStages}
        members={project.members}
        departments={activeDepartments}
      />
      {workflowAttempted && <RequirementHint missing={workflowMissing} />}
      {error && <div className="error-box">{error}</div>}
      <footer>
        {workflow && <button type="button" className="danger-quiet" onClick={() => setDeactivateConfirm(true)}><Power size={13} /> 워크플로우 비활성화</button>}
        <span className="footer-spacer" />
        <button onClick={() => setEditing(false)}>취소</button>
        <button className="primary" disabled={busy} onClick={event => { setWorkflowAttempted(true); if (workflowMissing.length) { focusInvalidField(event.currentTarget.closest(".modal")!); return; } void saveWorkflow(); }}>{busy ? "저장 중…" : "새 버전 저장"}</button>
      </footer>
    </div></ModalFrame>}

    {deactivateConfirm && <ConfirmDialog title="현재 워크플로우를 비활성화할까요?" eyebrow="DEACTIVATE WORKFLOW" confirmLabel="워크플로우 비활성화" busy={busy} onClose={() => setDeactivateConfirm(false)} onConfirm={async () => {
      setBusy(true); setError("");
      try { await api.deactivateProjectWorkflow(project.id); setDeactivateConfirm(false); setEditing(false); await load(); }
      catch (reason) { setError(reason instanceof Error ? reason.message : "워크플로우를 비활성화하지 못했습니다."); }
      finally { setBusy(false); }
    }}>
      <p>기존 업무는 생성 당시 단계와 이력을 계속 보존합니다. 새 워크플로우를 만들기 전까지 프로젝트 업무는 단계 없이만 생성할 수 있습니다.</p>
    </ConfirmDialog>}

    {confirming && <ConfirmDialog
      title={t(confirming === "COMPLETED" ? "projects.completeQuestion" : "projects.cancelQuestion", { name: project.name })}
      eyebrow={confirming === "COMPLETED" ? "COMPLETE PROJECT" : "CANCEL PROJECT"}
      confirmLabel={t(confirming === "COMPLETED" ? "projects.complete" : "projects.cancel")} busy={busy}
      onClose={() => setConfirming(null)} onConfirm={async () => {
        setBusy(true); setError("");
        try {
          const updated = await api.changeProjectStatus(project.id, confirming);
          setProject(updated); onChanged?.(updated); setConfirming(null);
        }
        catch (reason) { setError(reason instanceof Error ? reason.message : t("projects.changeError")); setConfirming(null); }
        finally { setBusy(false); }
      }}>
      <p>{t(confirming === "COMPLETED" ? "projects.completeHelp" : "projects.cancelHelp")}</p>
    </ConfirmDialog>}

    {deleteConfirm && <ConfirmDialog title={`${project.name}을 삭제할까요?`} eyebrow="DELETE PROJECT" confirmLabel="프로젝트 삭제" busy={busy} onClose={() => setDeleteConfirm(false)} onConfirm={async () => {
      setBusy(true); setError("");
      try { await api.deleteProject(project.id); setDeleteConfirm(false); onBack(); }
      catch (reason) { setError(reason instanceof Error ? reason.message : "프로젝트를 삭제하지 못했습니다."); setDeleteConfirm(false); }
      finally { setBusy(false); }
    }}>
      <p>프로젝트는 목록에서 사라지지만 완료된 업무·문서·감사 기록은 보존됩니다. 이 작업은 화면에서 되돌릴 수 없습니다.</p>
    </ConfirmDialog>}

    {overviewEditing && <ModalFrame title="개요 수정" eyebrow="OVERVIEW" onClose={() => setOverviewEditing(false)}><form className="modal-form" onSubmit={async (event) => {
      event.preventDefault(); setBusy(true); setError("");
      const form = new FormData(event.currentTarget);
      try {
        await api.updateProject(project.id, {
          name: String(form.get("name")),
          startsOn: String(form.get("startsOn")),
          endsOn: String(form.get("endsOn")),
          projectType: String(form.get("projectType") || "") || null,
          goal: String(form.get("goal") || "") || null,
          description: String(form.get("description") || "") || null,
        });
        setOverviewEditing(false); await load();
      } catch (reason) { setError(reason instanceof Error ? reason.message : "개요를 저장하지 못했습니다."); }
      finally { setBusy(false); }
    }}>
      <label className="span-2">제목<input name="name" required maxLength={240} defaultValue={project.name} /></label>
      <label>종류<input name="projectType" maxLength={80} placeholder="일반 프로젝트" defaultValue={project.projectType || ""} /></label>
      <label>기간<span className="date-pair"><input name="startsOn" type="date" required defaultValue={isoDate(project.startsOn)} /><input name="endsOn" type="date" required defaultValue={isoDate(project.endsOn)} /></span></label>
      <label className="span-2">목표<textarea name="goal" rows={3} maxLength={2000} defaultValue={project.goal || ""} /></label>
      <label className="span-2">설명<textarea name="description" rows={4} maxLength={2000} defaultValue={project.description || ""} /></label>
      {error && <div className="error-box span-2">{error}</div>}
      <footer className="span-2"><button type="button" onClick={() => setOverviewEditing(false)}>취소</button><button className="primary" disabled={busy}>{busy ? "저장 중…" : "저장"}</button></footer>
    </form></ModalFrame>}

    {resourceAdding && <ModalFrame title="외부 링크 추가" eyebrow="PROJECT LIBRARY" onClose={() => setResourceAdding(false)}><form className="modal-form" onSubmit={async (event) => {
      event.preventDefault(); setBusy(true); setError("");
      const form = new FormData(event.currentTarget);
      try {
        await api.addProjectLink(project.id, { title: String(form.get("title")), label: String(form.get("label") || "") || null, externalUrl: String(form.get("externalUrl")) });
        setResourceAdding(false); await load();
      } catch (reason) { setError(reason instanceof Error ? reason.message : "문서를 추가하지 못했습니다."); }
      finally { setBusy(false); }
    }}>
      <label>문서 라벨<input name="label" maxLength={80} placeholder="기획, 규정" /></label>
      <label className="span-2">표시 제목<input name="title" required maxLength={240} /></label>
      <label className="span-2">URL<input name="externalUrl" type="url" required placeholder="https://" /></label>
      {error && <div className="error-box span-2">{error}</div>}
      <footer className="span-2"><button type="button" onClick={() => setResourceAdding(false)}>취소</button><button className="primary" disabled={busy}>{busy ? "추가 중…" : "문서 추가"}</button></footer>
    </form></ModalFrame>}
  </main>;
}

const budgetFields = [
  { key: "labor", label: "인건비" },
  { key: "material", label: "재료비" },
  { key: "outsourcing", label: "외주비" },
  { key: "other", label: "기타 경비" },
] as const;

function money(value: number, currency: string) {
  return new Intl.NumberFormat("ko-KR", { style: "currency", currency, maximumFractionDigits: 0 }).format(value);
}

function budgetCommand(budget: ProjectBudget): ProjectBudgetCommand {
  return {
    currency: budget.currency,
    laborBudget: budget.laborBudget, materialBudget: budget.materialBudget, outsourcingBudget: budget.outsourcingBudget, otherBudget: budget.otherBudget,
    laborCost: budget.laborCost, materialCost: budget.materialCost, outsourcingCost: budget.outsourcingCost, otherCost: budget.otherCost,
    notes: budget.notes || null,
  };
}

function budgetTotals(budget: ProjectBudgetCommand) {
  const totalBudget = budgetFields.reduce((sum, { key }) => sum + Number(budget[`${key}Budget`] || 0), 0);
  const totalCost = budgetFields.reduce((sum, { key }) => sum + Number(budget[`${key}Cost`] || 0), 0);
  return { totalBudget, totalCost, balance: totalBudget - totalCost, ratio: totalBudget > 0 ? Math.round(totalCost / totalBudget * 100) : 0 };
}

export function ProjectBudgetPage({ session, permissions = [], projects, onBack }: { session: Session; permissions?: AdditionalPermission[]; projects: Project[]; onBack: () => void }) {
  const canViewAll = session.roles.includes("OWNER") || permissions.includes("PROJECT_BUDGET_VIEW") || permissions.includes("PROJECT_BUDGET_EDIT");
  const canEditAll = session.roles.includes("OWNER") || permissions.includes("PROJECT_BUDGET_EDIT");
  const manageable = useMemo(() => projects.filter((project) => canViewAll
    || project.members.some((member) => member.userId === session.userId && member.role === "PM")), [canViewAll, projects, session.userId]);
  const [budgets, setBudgets] = useState<Record<string, ProjectBudget>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editError, setEditError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const entries = await Promise.all(manageable.map(async (project) => [project.id, await api.projectBudget(project.id)] as const));
      const loaded = Object.fromEntries(entries) as Record<string, ProjectBudget>;
      setBudgets(loaded);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "예산 현황을 불러오지 못했습니다."); }
    finally { setLoading(false); }
  }, [manageable]);
  useEffect(() => { void load(); }, [load]);

  return <>
    <div className="breadcrumb"><button type="button" onClick={onBack}>← 프로젝트 목록</button></div>
    <PageHead title="예산 관리" meta="프로젝트별 예산과 누적 집행액을 관리합니다. 항목을 눌러 상세 내역을 펼칠 수 있습니다." />
    {error && <div className="global-error budget-page-error">{error}<button type="button" onClick={() => setError("")}>닫기</button></div>}
    {loading ? <Loading layout="summary" label="예산 현황을 불러오는 중" /> : manageable.length === 0
      ? <div className="table-card"><Empty title="관리할 프로젝트가 없습니다" copy="본인이 PM인 프로젝트가 생기면 이곳에서 예산을 관리할 수 있습니다." /></div>
      : <div className="budget-project-list">
        <div className="budget-project-list-head"><span>프로젝트</span><span>편성 예산</span><span>누적 집행</span><span>잔액</span><span>집행률</span></div>
        {manageable.map((project) => {
          const budget = budgets[project.id];
          if (!budget) return null;
          const totals = budgetTotals(budget);
          const open = expanded.has(project.id);
          const canEdit = canEditAll || project.members.some((member) => member.userId === session.userId && member.role === "PM");
          return <section className="budget-project" key={project.id}>
            <button type="button" className="budget-project-summary" aria-expanded={open} onClick={() => setExpanded((current) => {
              const next = new Set(current); if (next.has(project.id)) next.delete(project.id); else next.add(project.id); return next;
            })}>
              <span className="budget-project-cell"><b>{project.name}</b><small>{project.code} · {statusText[project.status]}</small></span>
              <span className="mono">{money(totals.totalBudget, budget.currency)}</span>
              <span className="mono">{money(totals.totalCost, budget.currency)}</span>
              <span className={`mono ${totals.balance < 0 ? "warn" : ""}`}>{money(totals.balance, budget.currency)}</span>
              <span className="budget-ratio"><Meter ratio={totals.ratio} /><em className={totals.ratio > 100 ? "warn" : ""}>{totals.ratio}%</em></span>
              <i aria-hidden="true">⌄</i>
            </button>
            {open && <div className="budget-project-detail">
              <div className="budget-detail-tools"><span>통화 <b>{budget.currency}</b></span>{project.status === "ACTIVE" && canEdit
                ? <button type="button" className="secondary" onClick={() => { setEditError(""); setEditingProject(project); }}><Pencil size={13} /> 수정</button>
                : <span>{project.status !== "ACTIVE" ? "종료된 프로젝트는 조회만 가능합니다." : "조회 권한만 부여되어 있습니다."}</span>}</div>
              <div className="budget-detail-table">
                <div className="budget-detail-row budget-detail-head"><span>구분</span><span>예산</span><span>누적 집행</span><span>잔액</span></div>
                {budgetFields.map(({ key, label }) => {
                  const planned = Number(budget[`${key}Budget`] || 0);
                  const spent = Number(budget[`${key}Cost`] || 0);
                  return <div className="budget-detail-row" key={key}><b>{label}</b>
                    <span>{money(planned, budget.currency)}</span>
                    <span>{money(spent, budget.currency)}</span>
                    <span className={planned - spent < 0 ? "warn" : ""}>{money(planned - spent, budget.currency)}</span>
                  </div>;
                })}
              </div>
              <div className="budget-notes-view"><b>관리 메모</b><p>{budget.notes || "등록된 메모가 없습니다."}</p></div>
            </div>}
          </section>;
        })}
      </div>}
    {editingProject && budgets[editingProject.id] && <ProjectBudgetEditModal
      key={editingProject.id}
      project={editingProject} budget={budgets[editingProject.id]} busy={busy} error={editError}
      onClose={() => { if (!busy) setEditingProject(null); }}
      onSave={async (command) => {
        setBusy(true); setEditError("");
        try {
          const saved = await api.updateProjectBudget(editingProject.id, command);
          setBudgets((current) => ({ ...current, [editingProject.id]: saved }));
          setEditingProject(null);
        } catch (reason) { setEditError(reason instanceof Error ? reason.message : "예산을 저장하지 못했습니다."); }
        finally { setBusy(false); }
      }}
    />}
  </>;
}

function ProjectBudgetEditModal({ project, budget, busy, error, onClose, onSave }: {
  project: Project; budget: ProjectBudget; busy: boolean; error: string;
  onClose: () => void; onSave: (command: ProjectBudgetCommand) => Promise<void>;
}) {
  const [draft, setDraft] = useState(() => budgetCommand(budget));
  const totals = budgetTotals(draft);
  const change = (values: Partial<ProjectBudgetCommand>) => setDraft((current) => ({ ...current, ...values }));
  return <ModalFrame title={`${project.name} 예산 수정`} eyebrow="EDIT PROJECT FINANCE" wide onClose={onClose}>
    <form className="modal-form budget-edit-form" onSubmit={(event) => { event.preventDefault(); void onSave({ ...draft, notes: draft.notes?.trim() || null }); }}>
      <div className="budget-edit-summary span-2"><span><small>총 예산</small><strong>{money(totals.totalBudget, draft.currency)}</strong></span><span><small>누적 집행</small><strong>{money(totals.totalCost, draft.currency)}</strong></span><span><small>잔액</small><strong className={totals.balance < 0 ? "warn" : ""}>{money(totals.balance, draft.currency)}</strong></span></div>
      <label>통화<select value={draft.currency} onChange={(event) => change({ currency: event.target.value })}><option value="KRW">KRW · 원</option><option value="USD">USD · 달러</option><option value="EUR">EUR · 유로</option><option value="JPY">JPY · 엔</option></select></label>
      <span className="budget-help">이 프로젝트의 예산과 누적 집행액을 수정합니다.</span>
      <div className="budget-detail-table span-2">
        <div className="budget-detail-row budget-detail-head"><span>구분</span><span>예산</span><span>누적 집행</span><span>잔액</span></div>
        {budgetFields.map(({ key, label }) => {
          const planned = Number(draft[`${key}Budget`] || 0);
          const spent = Number(draft[`${key}Cost`] || 0);
          return <div className="budget-detail-row" key={key}><b>{label}</b>
            <span><input aria-label={`${project.name} ${label} 예산`} type="number" min="0" step="0.01" value={planned} onChange={(event) => change({ [`${key}Budget`]: Number(event.target.value) })} /></span>
            <span><input aria-label={`${project.name} ${label} 누적 집행`} type="number" min="0" step="0.01" value={spent} onChange={(event) => change({ [`${key}Cost`]: Number(event.target.value) })} /></span>
            <span className={planned - spent < 0 ? "warn" : ""}>{money(planned - spent, draft.currency)}</span>
          </div>;
        })}
      </div>
      <label className="span-2">관리 메모<textarea rows={3} maxLength={2000} value={draft.notes || ""} onChange={(event) => change({ notes: event.target.value || null })} placeholder="집행 기준이나 변경 사유를 기록하세요." /></label>
      {error && <div className="error-box span-2">{error}</div>}
      <footer className="span-2"><button type="button" onClick={onClose}>취소</button><button type="submit" className="primary" disabled={busy}>{busy ? "저장 중…" : "저장"}</button></footer>
    </form>
  </ModalFrame>;
}

export function ProjectCreate({ users, departments, onClose, onCreated }: { users: User[]; departments: Department[]; onClose: () => void; onCreated: (project: Project) => void }) {
  const activeUsers = users.filter((user) => user.status === "ACTIVE");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pmId, setPmId] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [budgetOpen, setBudgetOpen] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError("");
    const form = new FormData(event.currentTarget);
    try {
      const budget = budgetOpen ? {
        currency: String(form.get("budgetCurrency") || "KRW"),
        laborBudget: Number(form.get("laborBudget") || 0), materialBudget: Number(form.get("materialBudget") || 0),
        outsourcingBudget: Number(form.get("outsourcingBudget") || 0), otherBudget: Number(form.get("otherBudget") || 0),
        laborCost: 0, materialCost: 0, outsourcingCost: 0, otherCost: 0,
        notes: String(form.get("budgetNotes") || "") || null,
      } : undefined;
      onCreated(await api.createProject({
        code: form.get("code") || null, name: form.get("name"), startsOn: form.get("startsOn"), endsOn: form.get("endsOn"),
        goal: form.get("goal") || null, description: form.get("description") || null, projectType: form.get("projectType") || null,
        members: [{ userId: pmId, role: "PM" }, ...memberIds.filter((id) => id !== pmId).map((userId) => ({ userId, role: "MEMBER" }))],
        participatingDepartmentIds: departmentIds,
        ...(budget ? { budget } : {}),
      }));
    } catch (reason) { setError(reason instanceof Error ? reason.message : "프로젝트를 만들지 못했습니다."); setBusy(false); }
  }
  return <ModalFrame title="프로젝트 생성" eyebrow="NEW PROJECT" onClose={onClose}><form className="modal-form" onSubmit={submit}>
    <label>프로젝트 코드<input name="code" maxLength={64} placeholder="비워두면 자동 부여" /></label>
    <label>프로젝트 유형<input name="projectType" maxLength={80} placeholder="R&D, 제품 개발" /></label>
    <label className="span-2">프로젝트 이름<input name="name" required maxLength={240} autoFocus /></label>
    <label>시작일<input name="startsOn" type="date" required /></label><label>종료일<input name="endsOn" type="date" required /></label>
    <label className="span-2">목표<textarea name="goal" rows={2} maxLength={2000} placeholder="이 프로젝트가 끝났을 때 무엇이 달라지는가" /></label>
    <label className="span-2">설명<textarea name="description" rows={4} maxLength={2000} placeholder="범위와 주요 맥락" /></label>
    <UserPicker label="PM" name="pm" required users={activeUsers} value={pmId ? [pmId] : []} onChange={ids => setPmId(ids[0] || "")} />
    <ProjectMemberPicker users={activeUsers} selectedIds={memberIds} excludedId={pmId} onChange={setMemberIds} />
    <ProjectDepartmentPicker departments={departments.filter((department) => department.status === "ACTIVE")} selectedIds={departmentIds} onChange={setDepartmentIds} />
    <details className="project-budget-create span-2" onToggle={(event) => setBudgetOpen(event.currentTarget.open)}>
      <summary><span><b>예산</b><small>선택 입력 · PM과 별도 권한 보유자만 조회</small></span><em>{budgetOpen ? "접기" : "입력하기"}</em></summary>
      <div className="project-budget-create-body">
        <label>통화<select name="budgetCurrency" defaultValue="KRW"><option value="KRW">KRW · 원</option><option value="USD">USD · 달러</option><option value="EUR">EUR · 유로</option><option value="JPY">JPY · 엔</option></select></label>
        <span className="budget-help">실제 집행액은 프로젝트 생성 후 예산 관리에서 기록할 수 있습니다.</span>
        {budgetFields.map(({ key, label }) => <label key={key}>{label} 예산<input name={`${key}Budget`} type="number" min="0" step="0.01" defaultValue="0" /></label>)}
        <label className="span-2">관리 메모<textarea name="budgetNotes" rows={2} maxLength={2000} placeholder="예산 기준이나 참고사항" /></label>
      </div>
    </details>
    {error && <div className="error-box span-2">{error}</div>}
    <footer className="span-2"><button type="button" onClick={onClose}>취소</button><button className="primary" disabled={busy}>{busy ? "생성 중…" : "프로젝트 생성 후 워크플로우 설계"}</button></footer>
  </form></ModalFrame>;
}

export function ProjectMemberPicker({ users, selectedIds, excludedId, onChange }: { users: User[]; selectedIds: string[]; excludedId?: string; onChange: (ids: string[]) => void }) {
  return <UserPicker label="참여자" users={users.filter(user => user.id !== excludedId)} value={selectedIds} multiple onChange={onChange} emptyText="아직 추가된 참여자가 없습니다." />;
}

export function ProjectDepartmentPicker({ departments, selectedIds, onChange }: { departments: Department[]; selectedIds: string[]; onChange: (ids: string[]) => void }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const byId = useMemo(() => new Map(departments.map((department) => [department.id, department])), [departments]);
  const path = (department: Department) => {
    const names = [department.name];
    const seen = new Set([department.id]);
    let parentId = department.parentId;
    while (parentId && !seen.has(parentId)) {
      seen.add(parentId);
      const parent = byId.get(parentId);
      if (!parent) break;
      names.unshift(parent.name); parentId = parent.parentId;
    }
    return names.join(" › ");
  };
  const normalized = query.trim().toLowerCase();
  const selected = selectedIds.map((id) => byId.get(id)).filter((department): department is Department => Boolean(department));
  const results = departments.filter((department) => !selectedIds.includes(department.id) && (!normalized || path(department).toLowerCase().includes(normalized)));
  return <div className="project-member-picker project-department-picker span-2" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false); }}>
    <span className="field-label">참여 부서</span>
    <div className="member-chips">{selected.length ? selected.map((department) => <span key={department.id}><b>{department.name}</b><small>{path(department)}</small><button type="button" aria-label={`${department.name} 제거`} onClick={() => onChange(selectedIds.filter((id) => id !== department.id))}><X size={12} /></button></span>) : <em>아직 추가된 참여 부서가 없습니다.</em>}</div>
    <div className="member-search"><Search size={14} /><input aria-label="참여 부서 검색" value={query} onFocus={() => setOpen(true)} onChange={(event) => { setQuery(event.target.value); setOpen(true); }} placeholder="부서 이름으로 검색" /></div>
    {open && <div className="member-search-results">{results.length ? results.map((department) => <button type="button" key={department.id} onClick={() => { onChange([...selectedIds, department.id]); setQuery(""); }}><span className="avatar">{initials(department.name)}</span><span><b>{department.name}</b><small>{path(department)}</small></span><em>추가</em></button>) : <p>검색 결과가 없습니다.</p>}</div>}
  </div>;
}

/**
 * 문서 작성 화면. 모달이 아니라 프로젝트 상세 대신 그려지는 전체 화면이다 — 본문을 길게 쓰는
 * 일이라 좁은 모달로는 부족하다. 블록 편집기로 작성하고 기존 문자열 API로 저장한다.
 */
function DocumentComposer({ draft, projectName, busy, error, onChange, onCancel, onSave }: {
  draft: DocumentDraft;
  projectName: string;
  busy: boolean;
  error: string;
  onChange: (next: DocumentDraft) => void;
  onCancel: () => void;
  onSave: (draft: DocumentDraft) => void;
}) {
  const [attempted, setAttempted] = useState(false);
  return <main className="page doc-composer">
    <div className="page-head detail-head">
      <div>
        <div className="breadcrumb"><button type="button" onClick={onCancel}>← {projectName}</button></div>
        <h1>{draft.id ? "문서 수정" : "문서 작성"}</h1>
      </div>
      <div className="page-head-controls">
        <button type="button" onClick={onCancel}>취소</button>
        <button className="primary" disabled={busy} onClick={event => { setAttempted(true); if (!draft.title.trim()) { focusInvalidField(event.currentTarget.closest("main")!); return; } onSave(draft); }}>{busy ? "저장 중…" : draft.id ? "변경 저장" : "문서 저장"}</button>
      </div>
    </div>

    <section className="section-card">
      <div className="doc-composer-meta">
        <label className="doc-composer-title"><span>제목 <em className="required-mark">필수</em></span><input aria-label="제목" aria-invalid={attempted && !draft.title.trim()} value={draft.title} required maxLength={240} placeholder="문서 제목" onChange={(event) => onChange({ ...draft, title: event.target.value })} /></label>
        <label>문서 라벨<input value={draft.label} maxLength={80} placeholder="기획, 규정" onChange={(event) => onChange({ ...draft, label: event.target.value })} /></label>
      </div>
      <div className="doc-composer-body">
        <BlockEditor format="markdown" id="project-document" ariaLabel="본문" value={draft.body} onChange={(body) => onChange({ ...draft, body })} />
      </div>
      {attempted && <RequirementHint className="page-requirement" missing={draft.title.trim() ? [] : ["제목"]} />}
      {error && <div className="error-box">{error}</div>}
    </section>
  </main>;
}

/** 팔레트에서 끌어다 놓는 단계 종류. 완료는 항상 마지막에 고정이라 팔레트에 없다. */
const STAGE_PALETTE: { kind: WorkflowStageKind; help: string }[] = [
  { kind: "WORK", help: "담당자가 결과를 만드는 단계" },
  { kind: "REVIEW", help: "결과가 기준을 충족하는지 확인" },
  { kind: "DECISION", help: "지시자가 승인하거나 보완을 요청" },
  { kind: "TEAM", help: "담당 팀이 여러 개인 업무로 나누어 실행" },
];

const newStage = (kind: WorkflowStageKind): WorkflowStageCommand =>
  ({ clientKey: `stage-${crypto.randomUUID()}`, name: workflowKindText[kind], kind, assigneeIds: [], departmentId: null });

/**
 * 워크플로우 설계 캔버스. 팔레트에서 끌어 놓아 단계를 만들고, 노드 머리를 잡아 순서를 바꾼다.
 * 담당 지정은 노드 안에서 끝낸다 — 일반 단계는 프로젝트 참여자 중에서, 팀 실행 단계는 팀 하나만
 * 고를 수 있다. 백엔드도 같은 규칙을 강제한다(ProjectWorkflowService.validate).
 * 완료 단계는 옮기거나 지울 수 없고 담당도 갖지 않는다.
 */
export function WorkflowCanvas({ stages, onChange, members, departments }: {
  stages: WorkflowStageCommand[];
  onChange: (next: WorkflowStageCommand[]) => void;
  members: { userId: string; displayName: string }[];
  departments: Department[];
}) {
  const [dragKind, setDragKind] = useState<WorkflowStageKind | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);

  const completeAt = stages.findIndex((stage) => stage.kind === "COMPLETE");
  const limit = completeAt < 0 ? stages.length : completeAt;
  const departmentName = (id?: string | null) => departments.find((department) => department.id === id)?.name;

  const clearDrag = () => { setDragKind(null); setDragKey(null); setDropAt(null); };
  const insert = (at: number, stage: WorkflowStageCommand) => {
    const target = Math.min(at, limit);
    onChange([...stages.slice(0, target), stage, ...stages.slice(target)]);
  };
  const move = (key: string, at: number) => {
    const from = stages.findIndex((stage) => stage.clientKey === key);
    if (from < 0) return;
    const without = stages.filter((stage) => stage.clientKey !== key);
    const end = without.findIndex((stage) => stage.kind === "COMPLETE");
    const target = Math.min(at > from ? at - 1 : at, end < 0 ? without.length : end);
    onChange([...without.slice(0, target), stages[from], ...without.slice(target)]);
  };
  const drop = (at: number) => {
    if (dragKind) insert(at, newStage(dragKind));
    else if (dragKey) move(dragKey, at);
    clearDrag();
  };
  const patch = (key: string, next: Partial<WorkflowStageCommand>) =>
    onChange(stages.map((stage) => stage.clientKey === key ? { ...stage, ...next } : stage));

  return <div className="wf-designer">
    <aside className="wf-palette">
      <p className="wf-palette-note">단계 팔레트 — 캔버스로 끌어다 놓으세요</p>
      {STAGE_PALETTE.map((item) => <div
        key={item.kind}
        className={`wf-chip kind-${item.kind.toLowerCase()}`}
        draggable
        role="button"
        tabIndex={0}
        aria-label={`${workflowKindText[item.kind]} 단계 추가`}
        onDragStart={() => { setDragKind(item.kind); setDragKey(null); }}
        onDragEnd={clearDrag}
        onDoubleClick={() => insert(limit, newStage(item.kind))}
        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); insert(limit, newStage(item.kind)); } }}
      >
        <span className="wf-chip-name">{workflowKindText[item.kind]}</span>
        <small>{item.help}</small>
      </div>)}
      <p className="wf-palette-hint">끌어다 놓거나 두 번 눌러 추가합니다.</p>
    </aside>

    <div className="wf-main">
      <div
        className={`wf-canvas${dragKind || dragKey ? " dropping" : ""}`}
        onDragOver={(event) => { event.preventDefault(); if (dropAt === null) setDropAt(limit); }}
        onDrop={(event) => { event.preventDefault(); drop(dropAt ?? limit); }}
        onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setDropAt(null); }}
      >
        <div className="wf-track">
          {stages.map((stage, index) => {
            const fixed = stage.kind === "COMPLETE";
            const team = stage.kind === "TEAM";
            const picked = members.filter((member) => (stage.assigneeIds || []).includes(member.userId));
            const summary = team
              ? departmentName(stage.departmentId) || "팀 미지정"
              : picked.map((member) => member.displayName).join(", ") || "담당 미지정";
            const missing = team ? !stage.departmentId : picked.length === 0;
            return <div className="wf-slot" key={stage.clientKey}>
              {dropAt === index && <span className="wf-drop-mark" aria-hidden />}
              <div className={`wf-node kind-${stage.kind.toLowerCase()}`}>
                <div
                  className="wf-node-head"
                  draggable={!fixed}
                  onDragStart={() => { setDragKey(stage.clientKey); setDragKind(null); }}
                  onDragEnd={clearDrag}
                  onDragOver={(event) => {
                    if (!dragKind && !dragKey) return;
                    event.preventDefault();
                    // 캔버스의 onDragOver가 이 위치를 끝으로 덮어쓰지 않도록 여기서 멈춘다.
                    event.stopPropagation();
                    const box = event.currentTarget.getBoundingClientRect();
                    setDropAt(Math.min(event.clientX < box.left + box.width / 2 ? index : index + 1, limit));
                  }}
                >
                  {!fixed && <GripVertical className="wf-grip" size={13} aria-hidden />}
                  <span className="wf-node-tag">{workflowKindText[stage.kind]}</span>
                  {!fixed && <button type="button" className="wf-node-remove" aria-label={`${stage.name} 단계 삭제`} onClick={() => onChange(stages.filter((item) => item.clientKey !== stage.clientKey))}><X size={12} /></button>}
                </div>

                {fixed
                  ? <p className="wf-node-fixed">업무를 붙일 수 없는 종료 표식입니다.</p>
                  : <>
                    <div className="wf-node-name">
                      <input value={stage.name} maxLength={120} aria-label={`${workflowKindText[stage.kind]} 단계 이름`} onChange={(event) => patch(stage.clientKey, { name: event.target.value })} />
                    </div>
                    <details className="wf-picker">
                      <summary className={missing ? "empty" : ""}><span>{summary}</span></summary>
                      {team
                        ? <div className="wf-options">
                          {departments.map((department) => <label key={department.id}>
                            <input
                              type="radio"
                              name={`team-${stage.clientKey}`}
                              checked={stage.departmentId === department.id}
                              onChange={() => patch(stage.clientKey, { departmentId: department.id, assigneeIds: [] })}
                            />
                            <span>{department.name}</span>
                          </label>)}
                          {departments.length === 0 && <p className="wf-options-empty">활성 팀이 없습니다.</p>}
                        </div>
                        : <div className="wf-options">
                          <UserPicker label="담당자" multiple users={members.map(member => ({ id: member.userId, displayName: member.displayName }))} value={stage.assigneeIds || []} onChange={ids => patch(stage.clientKey, { departmentId: null, assigneeIds: ids })} />
                          {members.length === 0 && <p className="wf-options-empty">프로젝트 참여자를 먼저 추가하세요.</p>}
                        </div>}
                    </details>
                  </>}
              </div>
              {index < stages.length - 1 && <span className="wf-link-arrow" aria-hidden />}
            </div>;
          })}
          {dropAt === limit && limit === stages.length && <span className="wf-drop-mark" aria-hidden />}
          <span className="wf-drop-hint">{dragKind ? `${workflowKindText[dragKind]} 놓기` : dragKey ? "여기로 옮기기" : "여기로 끌어다 놓으세요"}</span>
        </div>
      </div>

      {stages.some((stage) => stage.kind === "TEAM") && <p className="wf-team-note">
        팀이 담당인 단계가 활성화되면 해당 매니저가 실행 보드에서 <b>개인 업무를 여러 건</b> 만들어 팀원에게 배정합니다.
        개인 업무들은 서로 동등하게 이 실행 단계를 참조하고, 모두 종결되면 PM이 다음 단계로 넘깁니다. 고른 팀은 저장할 때 프로젝트 참여 부서로 함께 등록됩니다.
      </p>}
    </div>
  </div>;
}
